import axios, { AxiosRequestConfig } from 'axios'
import config from '@/router/config'
import getToken from '@/router/getToken'

const apiClient = axios.create( {
  baseURL: config.apiBase,
  timeout: 40000,
  params: {
    access_token: getToken()
  },
} )

type HttpErrorHandler = ( error: unknown ) => void

const defaultError: HttpErrorHandler = ( error ) => console.log( error )
const AUTH_ERROR_STATUSES = [ 401, 403 ]

interface HttpRequestOptions extends RequestInit {
  params?: Record<string, unknown>;
}

interface AuthResponse {
  access_token?: string;
  error?: string;
  message?: string;
  refresh_token?: string;
  [key: string]: unknown;
}

interface RequestStatusResponse {
  response?: {
    status?: number;
  };
}

class Http {

  private refreshRequest: Promise<boolean> | null = null

  private normalizeAuthResponse( response: unknown, fallbackError: string ): AuthResponse {
    if ( response && typeof response === 'object' ) {
      return response as AuthResponse
    }

    return {
      error: fallbackError
    }
  }

  private getErrorResponseData( error: unknown ): unknown {
    if ( axios.isAxiosError( error ) ) {
      return error.response?.data
    }

    return undefined
  }

  private hasAuthError( response: AuthResponse | null | undefined ): boolean {
    return ! response || Boolean( response.error )
  }

  private isAuthRequestError( error: unknown ): boolean {
    if ( ! axios.isAxiosError( error ) ) {
      return false
    }

    const status = error.response?.status
    return status ? AUTH_ERROR_STATUSES.includes( status ) : false
  }

  private storeTokens( response: AuthResponse ): boolean {
    if ( ! response.access_token || ! response.refresh_token ) {
      return false
    }

    localStorage.setItem( 'pltnmToken', response.access_token )
    localStorage.setItem( 'refreshToken', response.refresh_token )
    localStorage.setItem(
      'tokenExpiry',
      String( Math.round( new Date().getTime() / 1000 + 86400 ) )
    )

    this.defaultConfig.params.access_token = response.access_token
    return true
  }

  private clearStoredAuth() {
    localStorage.removeItem( 'pltnmToken' )
    localStorage.removeItem( 'refreshToken' )
    localStorage.removeItem( 'tokenExpiry' )
  }

  private redirectToLogin() {
    this.clearStoredAuth()
    window.location.href = '/login'
  }

  private async performRequestWithRefresh<T>(
    request: () => Promise<T>,
    cb: HttpErrorHandler = defaultError
  ): Promise<T> {
    try {
      return await request()
    } catch ( error ) {
      if ( this.isAuthRequestError( error ) && await this.validateRequest() ) {
        try {
          return await request()
        } catch ( retryError ) {
          if ( this.isAuthRequestError( retryError ) ) {
            this.redirectToLogin()
          }
          cb( retryError )
          throw retryError
        }
      }

      if ( this.isAuthRequestError( error ) ) {
        this.redirectToLogin()
      }

      cb( error )
      throw error
    }
  }

  private static async fetchClient<T>( url: string, options: HttpRequestOptions = {} ): Promise<T> {
    const response = await fetch( `${config.apiBase}/${url}`, options )

    if ( ! response.ok ) {
      console.log( 'response!', response )
      if ( response.status === 404 ) {
        const errorText = await response.text()
        throw new Error( `HTTP error! Status: ${response.status}, Message: ${errorText}` )
      }
    }

    return response.json() as Promise<T>
  }

  async postFetch<T,S>( url: string, data: S ): Promise<T> {
    let options: HttpRequestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add headers as needed
      },
      body: JSON.stringify( data ),
    }
    options = Http.addToken( options )
    return Http.fetchClient<T>( url, options )
  }

  static addToken( config: HttpRequestOptions ): HttpRequestOptions {
    const token = getToken() // Assuming getToken() retrieves your access token

    if ( ! config.params ) {
      config.params = {}
    }

    config.params.access_token = token
    return config
  }

  addToken = ( config: AxiosRequestConfig ) => {
    if ( config.params ) {
      config.params.access_token = getToken()
    } else {
      config.params = {}
      config.params.access_token = getToken()
    }
    return config
  }

  defaultConfig = {
    params: {
      access_token: getToken()
    }
  }

  /**
   * Destroy the session and log the user out.
   */
  logout = async () => {
    try {
      const response = await apiClient.post(
        `${config.rootURL}/oauth/destroy`,
        {
          oauth: 'me',
          access_token: getToken(),
        }
      )
      return response.data
    } catch ( error: unknown ) {
      return this.getErrorResponseData( error )
    }
  }

  /**
   * Check that the access token is valid.
   */
  validateSession = async () => {
    try {
      const response = await apiClient.get(
        config.rootURL,
        {
          params: {
            oauth: 'me',
            access_token: getToken(),
          }
        }
      )
      return this.normalizeAuthResponse( response.data, 'session_validation_failed' )
    } catch ( error: unknown ) {
      console.log( 'error', error )
      return this.normalizeAuthResponse( this.getErrorResponseData( error ), 'session_validation_failed' )
    }
  }

  /**
   * Check that the access token is valid.
   */
  getRefreshToken = async ( refreshToken: string ) => {
    try {
      const response = await axios( {
        method: 'post',
        url: config.authURI,
        headers: {
          'Authorization': 'Basic OFlVZVF5blZmOXJDT29WZWc3U1lsRnpKUGVYU2Rzc2puZ3hMVktScDpvS3pTTDRTYWR3Yk1KM2FMczU4RWRJZHVOZ1BkVWRnUHJNbHB4a1lz',
        },
        data: {
          'grant_type': 'refresh_token',
          'refresh_token': refreshToken,
        }
      } )
      return this.normalizeAuthResponse( response.data, 'token_refresh_failed' )
    } catch ( error: unknown ) {
      return this.normalizeAuthResponse( this.getErrorResponseData( error ), 'token_refresh_failed' )
    }
  }

  /**
   * Log the user in using OAuth2.
   *
   * @param email The user entered email address.
   * @param password The user entered password.
   */
  login = async ( email: string, password: string ) => {
    try {
      const response = await axios( {
        method: 'post',
        url: config.authURI,
        headers: {
          'Authorization': 'Basic OFlVZVF5blZmOXJDT29WZWc3U1lsRnpKUGVYU2Rzc2puZ3hMVktScDpvS3pTTDRTYWR3Yk1KM2FMczU4RWRJZHVOZ1BkVWRnUHJNbHB4a1lz',
        },
        data: {
          'grant_type': 'password',
          'username': email,
          'password': password
        }
      } )
      return response.data
    } catch ( error: unknown ) {
      return this.getErrorResponseData( error )
    }
  }

  /**
   * "POST" wrapper for our Http client.
   *
   * @param path The URL path to be queried.
   * @param data The data to be sent along with the request.
   * @param cb A callback function to execute on error.
   */
  post = async (
    path: string,
    data: unknown,
    config: AxiosRequestConfig = this.defaultConfig,
    cb: HttpErrorHandler = defaultError
  ) => {
    return this.performRequestWithRefresh( () => this.performPostRequest( path, data, config ), cb )
  }

  /**
   * Perform the actual request.
   *
   * @param path The URL path to be queried.
   * @param data The data to be sent along with the request.
   * @param config The inherited Axios config.
   * @returns The data from the response.
   */
  performPostRequest = async ( path: string, data: unknown, config: AxiosRequestConfig ) => {
    const response = await apiClient.post(
      path,
      data,
      this.addToken( config )
    )
    return response.data
  }

  validateRequest = async () => {
    const response = await this.validateSession()
    if ( ! this.hasAuthError( response ) ) {
      return true
    }

    if ( this.refreshRequest ) {
      return this.refreshRequest
    }

    this.refreshRequest = ( async () => {
      const refreshToken = localStorage.getItem( 'refreshToken' )
      if ( ! refreshToken ) {
        this.clearStoredAuth()
        return false
      }

      const refreshResponse = await this.getRefreshToken( refreshToken )
      if ( this.hasAuthError( refreshResponse ) || ! this.storeTokens( refreshResponse ) ) {
        this.clearStoredAuth()
        return false
      }

      return true
    } )()

    try {
      return await this.refreshRequest
    } finally {
      this.refreshRequest = null
    }
  }

  performGetRequest = async ( path: string, params: AxiosRequestConfig ) => {
    const response = await apiClient.get(
      path,
      this.addToken( params )
    )
    return response.data
  }

  /**
   * "GET" wrapper for our Http client.
   *
   * @param path The URL path to be queried.
   * @param AxiosRequestConfig The params for the "GET" request.
   * @param cb A callback function to execute on error.
   */
  get = async (
    path: string,
    params: AxiosRequestConfig = this.defaultConfig,
    cb: HttpErrorHandler = defaultError
  ) => {
    return this.performRequestWithRefresh( () => this.performGetRequest( path, params ), cb )
  }

  /**
   * Perform the actual request.
   *
   * @param path The URL path to be queried.
   * @param data The data to be sent along with the request.
   * @param config The inherited Axios config.
   * @returns The data from the response.
   */
  performPutRequest = async ( path: string, data: unknown ) => {
    const response = await apiClient.put(
      path,
      data,
      {
        params: {
          access_token: getToken()
        }
      }
    )
    return response.data
  }

  /**
   * Perform the actual request.
   *
   * @param path The URL path to be queried.
   * @param data The data to be sent along with the request.
   * @returns The data from the response.
   */
  performPatchRequest = async ( path: string, data: Record<string, unknown> ) => {
    const response = await apiClient.patch(
      path,
      data,
      {
        params: {
          access_token: getToken()
        }
      }
    )
    return response.data
  }

  /**
   * "PUT" wrapper for our Http client.
   *
   * @param path The URL path to be queried.
   * @param cb A callback function to execute on error.
   */
  put = async (
    path: string,
    data: Record<string, unknown>,
    cb: HttpErrorHandler = defaultError
  ) => {
    return this.performRequestWithRefresh( () => this.performPutRequest( path, data ), cb )
  }

  /**
   * "PATCH" wrapper for our Http client.
   *
   * @param path The URL path to be queried.
   * @param cb A callback function to execute on error.
   */
  patch = async (
    path: string,
    data: Record<string, unknown>,
    cb: HttpErrorHandler = defaultError
  ) => {
    return this.performRequestWithRefresh( () => this.performPatchRequest( path, data ), cb )
  }

  /**
   * Perform the actual request.
   *
   * @param path The URL path to be queried.
   * @param data The data to be sent along with the request.
   * @returns The data from the response.
   */
  performDeleteRequest = async ( path: string, data: Record<string, unknown>| null ) => {
    const response = await apiClient.delete(
      path,
      {
        data,
        params: {
          access_token: getToken()
        }
      }
    )
    return response.data
  }

  /**
   * "DELETE" wrapper for our Http client.
   *
   * @param path The URL path to be queried.
   * @param cb A callback function to execute on error.
   */
  delete = async (
    path: string,
    data: Record<string, unknown>| null = null,
    cb: HttpErrorHandler = defaultError
  ) => {
    return this.performRequestWithRefresh( () => this.performDeleteRequest( path, data ), cb )
  }

  requestSuccess( response: RequestStatusResponse ) {
    return response.response && response.response.status !== 200 && response.response.status !== 201
  }

}

export default new Http()
